import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

/**
 * Autosave dengan penundaan dan antrean satu slot.
 *
 * Tiga hal yang membuatnya aman dipakai di lapangan:
 *
 * 1. DITUNDA — mengetuk lima skor berturut-turut menghasilkan satu simpanan,
 *    bukan lima. Sinyal di lokasi TBM sering buruk; jumlah permintaan adalah
 *    hal pertama yang harus ditekan.
 * 2. TIDAK TUMPANG TINDIH — selama satu simpanan berjalan, perubahan baru
 *    ditahan di antrean lalu dikirim setelahnya. Dua permintaan paralel bisa
 *    tiba terbalik dan menulis balik nilai lama.
 * 3. DISIMPAN SAAT DITINGGAL — perubahan yang masih tertunda dikirim ketika
 *    halaman disembunyikan, bukan menunggu penundaan yang mungkin tak sempat.
 */
export function useAutosave<T>(
  save: (payload: T) => Promise<unknown>,
  delay = 900,
): {
  state: SaveState;
  schedule: (payload: T) => void;
  flush: () => Promise<void>;
} {
  const [state, setState] = useState<SaveState>('idle');
  const pending = useRef<T | null>(null);
  const running = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const run = useCallback(async () => {
    if (running.current || pending.current === null) return;

    running.current = true;
    const payload = pending.current;
    pending.current = null;
    setState('saving');

    try {
      await saveRef.current(payload);
      // Kalau ada perubahan baru yang masuk selagi menyimpan, statusnya belum
      // boleh "tersimpan" — masih ada yang menunggu dikirim.
      setState(pending.current === null ? 'saved' : 'pending');
    } catch {
      setState('error');
    } finally {
      running.current = false;
      if (pending.current !== null) void run();
    }
  }, []);

  const schedule = useCallback(
    (payload: T) => {
      pending.current = payload;
      setState('pending');
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void run(), delay);
    },
    [delay, run],
  );

  const flush = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    await run();
  }, [run]);

  useEffect(() => {
    const onHide = () => {
      if (pending.current !== null) void run();
    };
    // pagehide, bukan beforeunload: yang kedua tidak dipanggil saat tab
    // dibuang di peramban seluler — justru keadaan paling sering di lapangan.
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [run]);

  return { state, schedule, flush };
}
